import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  ArrowLeft,
  Edit,
  Trash2,
  ExternalLink,
  Package,
  GitBranch,
  Plus,
  X,
  Search,
  Layers,
  Users,
} from "lucide-react";
import { apiService } from "../services/api";
import { Application, Product, Repository, RepoContributor } from "../types";
import ApplicationDependencyTrackPanel from "../components/ApplicationDependencyTrackPanel";

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  deprecated:
    "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  retired: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
};

function formatDate(dateStr?: string) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLinkProducts, setShowLinkProducts] = useState(false);
  const [showLinkRepos, setShowLinkRepos] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [repoSearch, setRepoSearch] = useState("");

  const {
    data: appData,
    isLoading,
    isError,
  } = useQuery(["application", id], () => apiService.getApplication(id!), {
    enabled: !!id,
  });

  const app: Application | undefined = (appData?.data as any)?.data;

  const { data: productsData } = useQuery(
    ["products-for-link", productSearch],
    () => apiService.getProducts({ search: productSearch, limit: 50 }),
    { enabled: showLinkProducts },
  );
  const allProducts: Product[] =
    (productsData?.data as any)?.data?.products || [];

  const { data: reposData } = useQuery(
    ["repos-for-link", repoSearch],
    () => apiService.getRepositories({ search: repoSearch, limit: 50 }),
    { enabled: showLinkRepos },
  );
  const allRepos: Repository[] =
    (reposData?.data as any)?.data?.repositories || [];

  const deleteMutation = useMutation(() => apiService.deleteApplication(id!), {
    onSuccess: () => navigate("/applications"),
  });

  const linkProductsMutation = useMutation(
    (productIds: string[]) =>
      apiService.linkApplicationProducts(id!, productIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["application", id]);
        setShowLinkProducts(false);
      },
    },
  );

  const unlinkProductMutation = useMutation(
    (productId: string) => apiService.unlinkApplicationProduct(id!, productId),
    { onSuccess: () => queryClient.invalidateQueries(["application", id]) },
  );

  const linkReposMutation = useMutation(
    (repoIds: string[]) => apiService.linkApplicationRepositories(id!, repoIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["application", id]);
        setShowLinkRepos(false);
      },
    },
  );

  const unlinkRepoMutation = useMutation(
    (repoId: string) => apiService.unlinkApplicationRepository(id!, repoId),
    {
      onSuccess: () => queryClient.invalidateQueries(["application", id]),
    },
  );

  const linkedProductIds = new Set(app?.products?.map((p) => p.id) || []);
  const linkedRepoIds = new Set(app?.repositories?.map((r) => r.id) || []);

  const availableProducts = allProducts.filter(
    (p) => !linkedProductIds.has(p.id),
  );
  const availableRepos = allRepos.filter((r) => !linkedRepoIds.has(r.id));

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError || !app) {
    return (
      <div className="card p-12 text-center">
        <Layers className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          Application not found
        </h3>
        <Link
          to="/applications"
          className="mt-4 inline-flex items-center gap-2 text-primary-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Applications
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/applications"
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Applications
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {app.name}
            </h1>
            {app.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {app.description}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Link
              to={`/applications/${id}/edit`}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Edit className="h-4 w-4" /> Edit
            </Link>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="card p-4 mb-6 border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
          <p className="text-sm text-red-800 dark:text-red-200 mb-3">
            Are you sure you want to delete <strong>{app.name}</strong>? This
            action cannot be undone.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => deleteMutation.mutate()}
              className="btn bg-red-600 text-white hover:bg-red-700 text-sm"
            >
              Yes, delete
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Linked Products */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Package className="h-5 w-5" /> Products
                <span className="text-sm font-normal text-gray-500">
                  ({app.products?.length || 0})
                </span>
              </h2>
              <button
                onClick={() => setShowLinkProducts(true)}
                className="btn btn-primary text-sm flex items-center gap-1"
              >
                <Plus className="h-3.5 w-3.5" /> Link
              </button>
            </div>
            {showLinkProducts && (
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="input pl-10 w-full text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setShowLinkProducts(false);
                      setProductSearch("");
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    Close
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {availableProducts.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">
                      No products found.
                    </p>
                  ) : (
                    availableProducts.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => linkProductsMutation.mutate([p.id])}
                        className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center"
                      >
                        <span className="text-gray-900 dark:text-gray-100">
                          {p.name}
                        </span>
                        <Plus className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {!app.products?.length ? (
                <p className="px-6 py-8 text-center text-sm text-gray-500">
                  No products linked yet.
                </p>
              ) : (
                app.products.map((p) => (
                  <div
                    key={p.id}
                    className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <Link
                      to={`/products/${p.id}`}
                      className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {p.name}
                    </Link>
                    <button
                      onClick={() => unlinkProductMutation.mutate(p.id)}
                      className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Unlink"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Linked Repositories */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <GitBranch className="h-5 w-5" /> Repositories
                <span className="text-sm font-normal text-gray-500">
                  ({app.repositories?.length || 0})
                </span>
              </h2>
              <button
                onClick={() => setShowLinkRepos(true)}
                className="btn btn-primary text-sm flex items-center gap-1"
              >
                <Plus className="h-3.5 w-3.5" /> Link
              </button>
            </div>
            {showLinkRepos && (
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search repositories..."
                      value={repoSearch}
                      onChange={(e) => setRepoSearch(e.target.value)}
                      className="input pl-10 w-full text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setShowLinkRepos(false);
                      setRepoSearch("");
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    Close
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {availableRepos.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">
                      No repositories found.
                    </p>
                  ) : (
                    availableRepos.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => linkReposMutation.mutate([r.id])}
                        className="w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center"
                      >
                        <span className="text-gray-900 dark:text-gray-100">
                          {r.fullName}
                        </span>
                        <Plus className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {!app.repositories?.length ? (
                <p className="px-6 py-8 text-center text-sm text-gray-500">
                  No repositories linked yet.
                </p>
              ) : (
                app.repositories.map((r) => (
                  <div
                    key={r.id}
                    className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/repositories/${r.owner}/${r.name}`}
                        className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {r.fullName}
                      </Link>
                      <a
                        href={r.htmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                    <button
                      onClick={() => unlinkRepoMutation.mutate(r.id)}
                      className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Unlink"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Dependency-Track project links + CVEs */}
          <ApplicationDependencyTrackPanel applicationId={id!} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              Details
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">
                  Status
                </dt>
                <dd className="mt-0.5">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      STATUS_COLORS[app.status] || STATUS_COLORS.active
                    }`}
                  >
                    {app.status}
                  </span>
                </dd>
              </div>
              {app.applicationType && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">
                    Type
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                    {app.applicationType}
                  </dd>
                </div>
              )}
              {app.environment && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">
                    Environment
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                    {app.environment}
                  </dd>
                </div>
              )}
              {app.deploymentUrl && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">
                    Deployment URL
                  </dt>
                  <dd className="mt-0.5">
                    <a
                      href={app.deploymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                    >
                      {app.deploymentUrl}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </dd>
                </div>
              )}
              {app.owner && (
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">
                    Owner
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                    {app.owner.name}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">
                  Created
                </dt>
                <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(app.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500 dark:text-gray-400">
                  Updated
                </dt>
                <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(app.updatedAt)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Recent Contributors (aggregated from all repos) */}
          {(() => {
            const allContributors = (app.repositories || []).flatMap(
              (r) => r.contributors || [],
            );
            const byLogin = new Map<string, RepoContributor>();
            for (const c of allContributors) {
              const existing = byLogin.get(c.login);
              if (
                !existing ||
                new Date(c.commitDate) > new Date(existing.commitDate)
              ) {
                byLogin.set(c.login, c);
              }
            }
            const uniqueContributors = [...byLogin.values()].sort(
              (a, b) =>
                new Date(b.commitDate).getTime() -
                new Date(a.commitDate).getTime(),
            );
            if (uniqueContributors.length === 0) return null;
            return (
              <div className="card p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Recent Contributors
                  <span className="text-xs font-normal">
                    ({uniqueContributors.length})
                  </span>
                </h3>
                <div className="space-y-3">
                  {uniqueContributors.map((contrib) => (
                    <div
                      key={contrib.login}
                      className="flex items-center gap-3"
                    >
                      <img
                        src={contrib.avatarUrl}
                        alt={contrib.login}
                        className="h-8 w-8 rounded-full"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {contrib.login}
                        </span>
                        {contrib.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {contrib.email}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(contrib.commitDate)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
